import { fetchEventSource } from '@microsoft/fetch-event-source'
import { type UseQueryOptions } from '@tanstack/vue-query'
import { computed, ref, unref } from 'vue'
import { nanoid } from 'nanoid'

import { useContext } from '@/composables/context'
import { useQueryClient, useQuery, useMutation } from '@/composables/vue-query'
import { tripQueryKeys } from '@/composables/trip'
import { eventModule } from '@/store'
import type {
  Conversation,
  DataMessage,
  Itinerary,
  ItineraryItem,
  Message,
  Place,
  Text,
  TextDelta,
} from '@/types/assistant'
import type { MaybeRef } from '@/types/types'
import { logEvent } from '@/utils/utility-manager'
import { isAnnotatablePlace } from '@/utils/place'

export type AssistantStatus = 'submitted' | 'streaming' | 'ready' | 'error'

type StandardEventSchema = {
  data: Record<string, any>
}

type StandardEventSchemas = Record<string, StandardEventSchema>

type EventPayload<TData = any> = {
  name: string
  data: TData
}

/**
 * A helper type to convert input schemas into the format expected by the
 * `EventSchemas` class, which ensures that each event contains all pieces
 * of information required.
 *
 * It purposefully uses slightly more complex (read: verbose) mapped types to
 * flatten the output and preserve comments.
 *
 * @public
 */
export type StandardEventSchemaToPayload<T> = {
  [K in keyof T & string]: {
    [K2 in keyof (Omit<EventPayload, keyof T[K]> & T[K] & { name: K })]: (Omit<
      EventPayload,
      keyof T[K]
    > &
      T[K] & { name: K })[K2]
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class EventSchemas<Schemas extends Record<string, EventPayload>> {
  fromRecord<EventSchemas extends StandardEventSchemas>() {
    return new EventSchemas<StandardEventSchemaToPayload<EventSchemas>>()
  }
}

type GetEvents<TInput> =
  TInput extends EventSchemas<infer U> ? U : Record<string, EventPayload>

type SendEventPayload<Events extends Record<string, EventPayload>> = {
  [K in keyof Events]: Events[K]
}[keyof Events]

export type JSONValue =
  | null
  | string
  | number
  | boolean
  | { [x: string]: JSONValue }
  | Array<JSONValue>

export type OnTextHandler = (options: {
  content: Text
  delta?: TextDelta
  done: boolean
}) => void

export interface UseAssistantOptions<
  TSchemas extends EventSchemas<Record<string, EventPayload>>,
> {
  /**
   * The API endpoint that accepts one ore more messages, starts a new thread run and
   * returns an `AssistantResponse` stream.
   */
  api: MaybeRef<string>

  /**
   * Additional headers to send to the API endpoint.
   */
  headers?: Record<string, string | undefined>

  schemas?: TSchemas

  /**
   * Callback function for when a new message is created
   */
  onMessage?: (message: Message<unknown>) => void

  /**
   * Callback function to handle updates of text based message blocks.
   */
  onText?: OnTextHandler

  /**
   * Callback function for data messages.
   */
  onData?: (message: DataMessage<SendEventPayload<GetEvents<TSchemas>>>) => void

  /**
   * Callback function for when a new place annotation is available.
   */
  onPlace?: (place: Place) => void
}

export function useAssistant<
  TSchemas extends EventSchemas<Record<string, EventPayload>>,
>(options: UseAssistantOptions<TSchemas>) {
  const { api, headers, onMessage, onText, onPlace } = options
  const isRunning = ref(false)
  const status = ref<AssistantStatus>('ready')

  function sendMessage(content: string) {
    isRunning.value = true

    status.value = 'submitted'

    fetchEventSource(unref(api), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        messages: [
          {
            id: nanoid(),
            role: 'user',
            content,
          },
        ],
      }),
      onmessage(event) {
        const data = JSON.parse(event.data)
        switch (event.event) {
          case 'message.created': {
            onMessage?.(data)
            break
          }
          case 'text.created': {
            status.value = 'streaming'
            onText?.({
              content: data,
              done: false,
            })
            break
          }
          case 'text.delta': {
            onText?.({
              content: data.snapshot,
              delta: data.delta,
              done: false,
            })
            break
          }
          case 'place.done': {
            onPlace?.(data.place)
          }
        }
      },
      onclose() {
        isRunning.value = false
        status.value = 'ready'
      },
      onerror(err) {
        isRunning.value = false
        status.value = 'error'
        throw err
      },
    })
  }

  return {
    sendMessage,
    isRunning,
    status,
  }
}

export function useCreateConversation() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async ({ tripId }: { tripId: string }) => {
      const { data } = await $axios.post<{
        conversation: {
          _id: string
          shortId: string
        }
      }>('/api/assistant/conversations', { tripId })

      return data
    },
  })
}

const conversationKeys = {
  all: ['conversations'] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
}

export function useConversation(
  conversationId: MaybeRef<string>,
  options: UseQueryOptions<Conversation> = {},
) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: conversationKeys.detail(unref(conversationId)),
    queryFn: async () => {
      const { data: conversation } = await $axios.get<Conversation>(
        `/api/assistant/conversations/${unref(conversationId)}`,
      )

      return conversation
    },
    ...options,
  })
}

export function useCreateOnboardingSession() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async () => {
      const { data } = await $axios.post<{
        sessionHandle: string
        secret: string
        conversation: Conversation
      }>('/api/assistant/onboarding')

      return data
    },
  })
}

export function useAddPlace() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: {
      placeId: string
      tripId: string
      waypointId?: string
    }) => {
      const { data } = await $axios.post<{
        items: ItineraryItem[]
        itinerary: Itinerary
      }>('/api/assistant/places', payload)
      return data
    },
  })
}

export function useRemoveItem() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: { tripId: string; item: ItineraryItem }) => {
      const { tripId, item } = payload
      const { data } = await $axios.post<{ itinerary: Itinerary }>(
        `/api/assistant/places/remove`,
        {
          tripId,
          item,
        },
      )
      return data
    },
  })
}

export function useClaimOnboardingSession() {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { sessionHandle: string; secret: string }) => {
      const { data } = await $axios.post<{ trip: { _id: string } }>(
        '/api/assistant/onboarding/claim',
        payload,
      )

      logEvent('assistant_onboarding_created_trip', {
        sessionHandle: payload.sessionHandle,
        tripId: data.trip._id,
      })

      return data
    },
    onSuccess: () => {
      eventModule.newMessage('success.trip.create')
      queryClient.invalidateQueries({ queryKey: tripQueryKeys.lists() })
    },
    onError: () => {
      eventModule.newError('error.trip.create')
    },
  })
}

export function usePlaceIcon(placeRef: MaybeRef<Place>) {
  const icon = computed(() => {
    const place = unref(placeRef)
    if (place.status !== 'active') {
      return '$marker'
    }

    if (place.type === 'location') {
      return '$marker'
    }

    if (place.categories.includes('beach')) {
      return '$beach'
    } else if (place.categories.includes('church')) {
      return '$churchOutline'
    } else if (place.categories.includes('island')) {
      return '$island'
    } else if (place.categories.includes('museum')) {
      return '$bankOutline'
    } else if (place.categories.includes('park')) {
      return '$treeOutline'
    }

    return '$marker'
  })

  return icon
}

export function usePlaceSubtitle(placeRef: MaybeRef<Place>) {
  const { i18n } = useContext()

  const subtitle = computed(() => {
    const place = unref(placeRef)
    if (isAnnotatablePlace(place)) {
      if (place.type === 'location') {
        if (place.category === 'country') {
          return i18n.t('country')
        } else if (place.category === 'city' || place.category === 'region') {
          return `${place.state}, ${place.country}`
        }
      }

      return `${place.city ?? place.state}, ${place.country}`
    }

    return i18n.t('place')
  })

  return subtitle
}
